AFRAME.registerComponent('financials-tab', {
    schema: {
        ticker: { type: 'string', default: "AAPL" },
        companyLogo: { type: 'string', default: '#company-logo-temp' },
        financialsData: { type: 'map', default: {} },
    },

    init: function () {
        // Find elements
        this.mainNavigation = this.el.querySelector('.financials-main-navigation');
        this.periodNavigation = this.el.querySelector('.financials-period-navigation');
        this.contentArea = this.el.querySelector('.financials-content-area');
        this.incomeContent = this.el.querySelector('.income-content');
        this.balanceContent = this.el.querySelector('.balance-content');
        this.cashflowContent = this.el.querySelector('.cashflow-content');
        this.noStockMessage = this.el.querySelector('.no-stock-selected-message');

        this.mainTabsList = [];
        this.periodTabsList = [];
        this.selectedMainTab = 0; // 0: Income, 1: Balance, 2: Cashflow
        this.selectedPeriodTab = 0; // 0: Annual, 1: Quarterly
        this.financialsPopup = null; // Reference to the popup element (deprecated, kept for compatibility)
        this.dockOriginalPosition = null; // Store original dock position for animation
        this.stock3dCubeWrapperOriginalPosition = null; // Store original stock-3d-cube-wrapper position for animation
        this.rightContainerOriginalState = null; // Store original right-container state (position, scale, rotation)
        this.isExpanded = false; // Track if panel is expanded
        this.tableContainer = null; // Reference to table container in content area

        // Initialize theme
        this.themeMode = 'Dark';
        try {
            // Check if getCacheItem is defined (it should be global)
            if (typeof getCacheItem === 'function') {
                const mainSettings = getCacheItem('mainSettings');
                if (mainSettings && mainSettings.themeMode) {
                    this.themeMode = mainSettings.themeMode;
                }
            }
        } catch (e) {
            console.warn('financials-tab: Failed to load theme settings', e);
        }

        // Use self for event listeners
        const self = this;

        // Listen for theme changes
        window.addEventListener('theme-changed', function (e) {
            if (e.detail && e.detail.themeMode) {
                self.themeMode = e.detail.themeMode;
                console.log('financials-tab: Theme changed to', self.themeMode);

                // Update UI based on new theme
                if (self.isExpanded) {
                    // If expanded, refresh the table (which will use new theme colors)
                    self.showFinancialsTable();
                } else {
                    // If collapsed, refresh the content area (blur text, expand button)
                    self.createContentArea();
                }
                
                // Re-run stock selection update to refresh text colors and visibility
                self.updateStockSelectionState();
            }
        });
        this.lastArrowClickTime = 0; // Track last arrow click time for throttle when panel is in center
        this.arrowClickThrottleTime = 1000; // Throttle time: 1 second (1000ms)

        if (!this.mainNavigation || !this.periodNavigation || !this.contentArea) {
            console.error('financials-tab: Some required elements are missing!');
        }

        this.createContent();

        // Re-find noStockMessage after createContent (in case it was cleared)
        if (!this.noStockMessage) {
            this.noStockMessage = this.el.querySelector('.no-stock-selected-message');
        }

        // Check stock selection state
        this.updateStockSelectionState();

        // Store reference to component for event handler
        // Store reference to component for event handler
        // self is already defined above

        // Listen for manual update events
        this.el.addEventListener('financials-data-updated', function (event) {
            if (!event.detail) {
                console.warn('financials-tab: No data in financials-data-updated event');
                return;
            }

            // Update component data from event
            if (event.detail.financialsData) {
                self.data.financialsData = event.detail.financialsData;
            }
            if (event.detail.ticker) {
                self.data.ticker = event.detail.ticker;
            }
            if (event.detail.companyLogo) {
                self.data.companyLogo = event.detail.companyLogo;
            }

            // Update stock selection state
            self.updateStockSelectionState();
            self.createContent();

            // If panel is expanded, update table with new data
            if (self.isExpanded && self.tableContainer) {
                self.updateFinancialsTable();
            }
        });

    },

    update: function (oldData) {
        if (!oldData || Object.keys(oldData).length === 0) {
            this.createContent();

            // Re-find noStockMessage after createContent
            if (!this.noStockMessage) {
                this.noStockMessage = this.el.querySelector('.no-stock-selected-message');
            }

            this.updateStockSelectionState();
            return;
        }

        // Check if data changed
        const dataChanged =
            this.data.ticker !== oldData.ticker ||
            this.data.companyLogo !== oldData.companyLogo ||
            JSON.stringify(this.data.financialsData) !== JSON.stringify(oldData.financialsData);

        if (dataChanged) {
            this.createContent();
        }
    },

    createContent: function () {

        // Reinitialize selectors if needed
        if (!this.mainNavigation) {
            this.mainNavigation = this.el.querySelector('.financials-main-navigation');
        }
        if (!this.periodNavigation) {
            this.periodNavigation = this.el.querySelector('.financials-period-navigation');
        }
        if (!this.contentArea) {
            this.contentArea = this.el.querySelector('.financials-content-area');
        }
        if (!this.incomeContent) this.incomeContent = this.el.querySelector('.income-content');
        if (!this.balanceContent) this.balanceContent = this.el.querySelector('.balance-content');
        if (!this.cashflowContent) this.cashflowContent = this.el.querySelector('.cashflow-content');

        if (!this.mainNavigation || !this.periodNavigation || !this.contentArea) {
            console.error('financials-tab: Cannot create content - required elements missing');
            return;
        }

        this.createMainNavigation();
        this.createPeriodNavigation();
        this.createContentArea();

        // Re-find noStockMessage after content creation
        if (!this.noStockMessage) {
            this.noStockMessage = this.el.querySelector('.no-stock-selected-message');
        }
    },

    createMainNavigation: function () {
        if (!this.mainNavigation) {
            console.warn('financials-tab: mainNavigation element not found');
            return;
        }

        // Clear existing buttons
        while (this.mainNavigation.firstChild) {
            this.mainNavigation.removeChild(this.mainNavigation.firstChild);
        }
        this.mainTabsList = [];

        const buttonHeight = 0.018; // Slightly larger (75% of original 0.024)
        const spacing = 0.0075; // Slightly larger (75% of original 0.01)
        const startY = 0;
        const z = 0.03;
        const preferredFontSize = 0.00525; // Slightly larger (75% of original 0.007)
        const mainTabNames = ['Income Statement', 'Balance Sheet', 'Cash Flow Statement'];

        // Calculate button widths based on text length
        // Use proportional widths: shortest text gets base width, others scale proportionally
        const baseWidth = 0.045; // Base width for shortest text (75% of original 0.06)
        const padding = 0.006; // Horizontal padding on each side (75% of original 0.008)
        const maxWidth = 0.075; // Maximum width to prevent oversized buttons (75% of original 0.1)

        // Find shortest and longest text lengths
        const textLengths = mainTabNames.map(name => name.length);
        const minLength = Math.min(...textLengths);
        const maxLength = Math.max(...textLengths);

        // Calculate widths proportionally
        const buttonWidths = mainTabNames.map(name => {
            const length = name.length;
            // Scale from baseWidth for shortest to maxWidth for longest
            const ratio = minLength === maxLength ? 1 : (length - minLength) / (maxLength - minLength);
            const width = baseWidth + (maxWidth - baseWidth) * ratio;
            return Math.max(baseWidth, Math.min(maxWidth, width + padding * 2));
        });

        // Calculate total width and center offset
        const totalWidth = buttonWidths.reduce((sum, w) => sum + w, 0) + spacing * (buttonWidths.length - 1);
        const startX = -totalWidth / 2;

        // Create buttons with calculated widths
        mainTabNames.forEach((name, index) => {
            const buttonWidth = buttonWidths[index];
            const xOffset = index === 0 ? 0 : buttonWidths.slice(0, index).reduce((sum, w) => sum + w + spacing, 0);
            const x = startX + xOffset + buttonWidth / 2;
            const position = `${x} ${startY} ${z}`;

            const button = document.createElement("a-entity");
            button.setAttribute("ui-button", {
                label: name,
                type: "rounded-box",
                width: buttonWidth,
                height: buttonHeight,
                depth: 0.015, // Slightly larger (75% of original 0.02)
                colorScheme: 'primary',
                autosize: false, // Use calculated width instead
                isSelectable: true,
                radius: 0.00375, // Slightly larger (75% of original 0.005)
                preferredFontSize: preferredFontSize,
            });
            button.setAttribute("position", position);
            button.setAttribute("data-raycastable", "");
            this.mainTabsList.push(button);

            this.mainNavigation.appendChild(button);

            button.addEventListener("click", () => this.selectMainTab(button, index));
        });

        this.selectMainTab(this.mainTabsList[0], 0);
    },

    createPeriodNavigation: function () {
        if (!this.periodNavigation) {
            console.warn('financials-tab: periodNavigation element not found');
            return;
        }

        // Clear existing buttons
        while (this.periodNavigation.firstChild) {
            this.periodNavigation.removeChild(this.periodNavigation.firstChild);
        }
        this.periodTabsList = [];

        const buttonWidth = 0.05;
        const buttonHeight = buttonWidth * 0.4;
        const spacing = 0.01;
        const numberOfButtons = 2;

        // Calculate total width of all buttons
        const totalWidth = numberOfButtons * buttonWidth + (numberOfButtons - 1) * spacing;
        // Align buttons to the right edge (using smaller container width for better alignment)
        // Right edge is at containerWidth / 2, so start from there and go left
        const containerWidth = 0.3;
        const rightEdge = containerWidth / 2;
        // Start position: right edge minus total width
        const startX = rightEdge - totalWidth + buttonWidth / 6;
        const startY = 0;
        const z = 0.03;
        const periodTabNames = ['Annual', 'Quarterly'];

        periodTabNames.forEach((name, index) => {
            const x = startX + index * (buttonWidth + spacing);
            // Ensure x is a number before subtracting
            const adjustedX = typeof x === 'number' ? x - 0.02 : parseFloat(x) - 0.02;
            const position = `${adjustedX} ${startY} ${z}`;

            const button = document.createElement("a-entity");
            button.setAttribute("ui-button", {
                label: name,
                type: "rounded-box",
                width: buttonWidth,
                height: buttonHeight,
                depth: 0.02,
                colorScheme: 'primary',
                autosize: false,
                isSelectable: true,
                radius: 0.005,
                preferredFontSize: 0.008,
            });
            button.setAttribute("position", position);
            button.setAttribute("data-raycastable", "");
            this.periodTabsList.push(button);

            this.periodNavigation.appendChild(button);

            button.addEventListener("click", () => this.selectPeriodTab(button, index));
        });

        this.selectPeriodTab(this.periodTabsList[0], 0);
    },

    createContentArea: function () {
        if (!this.contentArea) {
            console.warn('financials-tab: contentArea element not found');
            return;
        }

        // Clear existing content
        this.contentArea.innerHTML = '';

        // Prepare table data
        const tableData = this.prepareTableData(this.data.financialsData, this.selectedMainTab, this.selectedPeriodTab);
        
        // Limit to 10 rows and 3 columns (Label, TTM, Period 1)
        const previewData = tableData.slice(0, 10).map(row => row.slice(0, 3)); 

        // Determine colors based on theme
        const isLight = this.themeMode === 'Light';
        const tableHeaderColor = isLight ? '#14141C' : '#ffffff';
        const tableHeaderBgColor = isLight ? '#E0E0E0' : '#222222';
        const tableRowColor1 = isLight ? '#ffffff' : '#1a1a1a';
        const tableRowColor2 = isLight ? '#f5f5f5' : '#151515';
        const tableTextColor = isLight ? '#14141C' : '#cccccc';

        // Create table container
        const previewTable = document.createElement('a-entity');
        previewTable.setAttribute('position', '0 0.01 0.01'); // Adjusted position lower (was 0.035)
        
        // Set table attributes
        previewTable.setAttribute('table', {
            width: 0.20, // Slightly narrower than full table
            rowHeight: 0.014,
            headerColor: tableHeaderColor,
            headerBgColor: tableHeaderBgColor,
            rowColor1: tableRowColor1,
            rowColor2: tableRowColor2,
            textColor: tableTextColor,
            data: JSON.stringify(previewData)
        });

        // Trigger render
        setTimeout(() => {
            if (previewTable.components && previewTable.components.table) {
                previewTable.components.table.renderTable();
            } else {
                // Retry if component not ready
               setTimeout(() => {
                    if (previewTable.components && previewTable.components.table) {
                        previewTable.components.table.renderTable();
                    }
               }, 200);
            }
        }, 100);

        this.contentArea.appendChild(previewTable);

        // Create overlay text first to reference it in event listeners
        const overlayText = document.createElement('a-text');
        overlayText.setAttribute('value', 'Click on Expand button\nto view full table');
        overlayText.setAttribute('scale', '0.04 0.04 0.04');
        overlayText.setAttribute('align', 'center');
        overlayText.setAttribute('anchor', 'center');
        overlayText.setAttribute('position', '0 -0.04 0.03'); // Match overlay position
        overlayText.setAttribute('color', '#ffffff');
        overlayText.setAttribute('visible', false); // Hidden by default
        this.contentArea.appendChild(overlayText);

        // Create Overlay for interaction hint
        const overlay = document.createElement('a-plane');
        overlay.setAttribute('width', 0.20);
        overlay.setAttribute('height', 0.14);
        overlay.setAttribute('color', '#000000');
        overlay.setAttribute('opacity', 0.01); // Almost transparent initially
        overlay.setAttribute('position', '0 -0.05 0.02'); // Moved down to match table
        overlay.setAttribute('class', 'clickable');
        overlay.setAttribute('data-raycastable', ''); // Ensure it's clickable
        
        // Hover effect for overlay
        overlay.addEventListener('mouseenter', () => {
             overlay.setAttribute('opacity', 0.4);
             overlayText.setAttribute('visible', true);
        });
        overlay.addEventListener('mouseleave', () => {
             overlay.setAttribute('opacity', 0.01);
             overlayText.setAttribute('visible', false);
        });
        
        // Block clicks on table
        overlay.addEventListener('click', (e) => {
            e.stopPropagation(); // Don't expand on table click
        });

        this.contentArea.appendChild(overlay);

        // Create Expand button
        const expandButton = document.createElement('a-entity');
        expandButton.setAttribute('ui-button', {
            label: 'Expand',
            type: 'rounded-box',
            width: 0.2,
            height: 0.03,
            depth: 0.02,
            colorScheme: 'primary',
            autosize: false,
            radius: 0.005,
            preferredFontSize: 0.01,
        });
        expandButton.setAttribute('position', '0 -0.15 0.04'); // Moved down and forward
        expandButton.setAttribute('id', 'btn-expand');
        expandButton.setAttribute('data-raycastable', '');

        // Add icon to button
        const expandIcon = document.createElement('a-image');
        expandIcon.setAttribute('src', './vr-assets/right-container-buttons/Expand.png');
        expandIcon.setAttribute('width', 0.01);
        expandIcon.setAttribute('height', 0.01);
        expandIcon.setAttribute('transparent', true);
        expandIcon.setAttribute('material', 'transparent: true; alphaTest: 0.5;');
        expandIcon.setAttribute('position', '0.08 0 0.03');
        expandButton.appendChild(expandIcon);

        expandButton.addEventListener('click', () => {
            console.log('financials-tab: Expand button clicked');
            this.expandFinancialsPanel();
        });

        this.contentArea.appendChild(expandButton);
    },

    selectMainTab: function (tabEl, selectedIndex) {
        this.mainTabsList.forEach((btn) => {
            if (btn && btn !== tabEl) {
                btn.setAttribute("ui-button", "selected", false);
                btn.setAttribute("scale", "1 1 1");
            }
        });
        tabEl.setAttribute("ui-button", "selected", true);
        tabEl.setAttribute("scale", "1.05 1.05 1.05");

        this.selectedMainTab = selectedIndex;
        console.log(`financials-tab: Selected main tab: ${selectedIndex}`);

        // Update content visibility
        this.updateContentVisibility();

        // Update table if panel is expanded
        if (this.isExpanded && this.tableContainer) {
            console.log('financials-tab: Main tab changed, updating table');
            this.updateFinancialsTable();
        }
    },

    selectPeriodTab: function (tabEl, selectedIndex) {
        this.periodTabsList.forEach((btn) => {
            if (btn && btn !== tabEl) {
                btn.setAttribute("ui-button", "selected", false);
                btn.setAttribute("scale", "1 1 1");
            }
        });
        tabEl.setAttribute("ui-button", "selected", true);
        tabEl.setAttribute("scale", "1.05 1.05 1.05");

        this.selectedPeriodTab = selectedIndex;
        console.log(`financials-tab: Selected period tab: ${selectedIndex}`);

        // Update content visibility
        this.updateContentVisibility();

        // Update table if panel is expanded
        if (this.isExpanded && this.tableContainer) {
            console.log('financials-tab: Period tab changed, updating table');
            this.updateFinancialsTable();
        }
    },

    updateContentVisibility: function () {
        // This will be used to show/hide different content based on selected tabs
        // For now, all content areas are hidden (will be shown when table is implemented)
        if (this.incomeContent) this.incomeContent.setAttribute('visible', false);
        if (this.balanceContent) this.balanceContent.setAttribute('visible', false);
        if (this.cashflowContent) this.cashflowContent.setAttribute('visible', false);
    },

    expandFinancialsPanel: function () {
        console.log('financials-tab: Expanding financials panel');
        const rightContainer = document.getElementById('right-container');
        if (!rightContainer) return;

        const expandComponent = rightContainer.components['right-panel-expand'];
        if (expandComponent) {
            expandComponent.expand(() => {
                this.showFinancialsTable();
                this.isExpanded = true;
            });
        }
    },

    collapseFinancialsPanel: function () {
        console.log('financials-tab: Collapsing financials panel');
        const rightContainer = document.getElementById('right-container');
        if (!rightContainer) return;

        const expandComponent = rightContainer.components['right-panel-expand'];
        if (expandComponent) {
            expandComponent.collapse(() => {
                this.hideFinancialsTable();
                this.isExpanded = false;
            });
        }
    },

    showFinancialsTable: function () {
        console.log('financials-tab: Showing financials table in panel');

        if (!this.contentArea) {
            console.error('financials-tab: contentArea not found');
            return;
        }

        // Clear existing content (blurFrame, text, button)
        this.contentArea.innerHTML = '';

        // Prepare table data
        const tableData = this.prepareTableData(this.data.financialsData, this.selectedMainTab, this.selectedPeriodTab);

        if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
            console.error('financials-tab: No valid table data!');
            const errorText = document.createElement('a-text');
            errorText.setAttribute('value', 'No financial data available');
            errorText.setAttribute('position', '0 0 0.01');
            errorText.setAttribute('color', '#ff0000');
            errorText.setAttribute('align', 'center');
            errorText.setAttribute('anchor', 'center');
            errorText.setAttribute('scale', '0.05 0.05 0.05');
            this.contentArea.appendChild(errorText);
            return;
        }

        // Determine colors based on theme
        const isLight = this.themeMode === 'Light';

        // Close button colors
        const closeBgColor = isLight ? '#000000' : '#191920';
        const closeTextColor = isLight ? '#000000' : '#ffffff';

        // Table colors
        const tableHeaderColor = isLight ? '#14141C' : '#ffffff'; // Text color
        const tableHeaderBgColor = isLight ? '#E0E0E0' : '#222222'; // Background color (New)
        const tableRowColor1 = isLight ? '#ffffff' : '#1a1a1a';
        const tableRowColor2 = isLight ? '#f5f5f5' : '#151515';
        const tableTextColor = isLight ? '#14141C' : '#cccccc';

        // Create close button at top
        const closeButton = document.createElement('a-entity');

        // Create background for button
        const closeBg = document.createElement('a-rounded-box');
        closeBg.setAttribute('width', '0.015');
        closeBg.setAttribute('height', '0.015');
        closeBg.setAttribute('depth', '0.01');
        closeBg.setAttribute('color', closeBgColor);
        closeBg.setAttribute('opacity', '0.1');
        closeBg.setAttribute('radius', '0.005');
        closeBg.setAttribute('position', '0 0 0');
        closeBg.setAttribute('data-raycastable', '');
        closeButton.appendChild(closeBg);

        // Create text for close symbol using a-troika-text for better visibility
        const closeText = document.createElement('a-troika-text');
        closeText.setAttribute('value', 'X');
        closeText.setAttribute('font-size', '0.006');
        closeText.setAttribute('color', closeTextColor);
        closeText.setAttribute('align', 'center');
        closeText.setAttribute('anchor', 'center');
        closeText.setAttribute('baseline', 'center');
        closeText.setAttribute('position', '0 0 0.03');
        closeText.setAttribute('data-raycastable', '');
        closeButton.appendChild(closeText);

        closeButton.setAttribute('position', '0.115 0.135 0.01');
        closeButton.setAttribute('id', 'btn-close-financials');
        closeButton.setAttribute('data-raycastable', '');
        closeButton.addEventListener('click', () => {
            console.log('financials-tab: Close button clicked');
            this.collapseFinancialsPanel();
        });
        this.contentArea.appendChild(closeButton);

        // Create table container
        this.tableContainer = document.createElement('a-entity');
        this.tableContainer.setAttribute('position', '0 0.005 0.01'); // Переміщено вище на 0.005

        // Set table attributes - adjust width for expanded panel
        this.tableContainer.setAttribute('table', {
            width: 0.24, // Зменшено ширину таблиці (було 0.25)
            rowHeight: 0.014, // Зменшено в 4 рази (0.06 / 4 = 0.015)
            headerColor: tableHeaderColor,
            headerBgColor: tableHeaderBgColor, // Pass new property
            rowColor1: tableRowColor1,
            rowColor2: tableRowColor2,
            textColor: tableTextColor,
            data: JSON.stringify(tableData)
        });

        // Store data in component and trigger render manually after component initializes
        setTimeout(() => {
            if (this.tableContainer.components && this.tableContainer.components.table) {
                this.tableContainer.components.table.tableData = tableData;
                this.tableContainer.setAttribute('table', 'data', JSON.stringify(tableData));
                this.tableContainer.components.table.renderTable();
                console.log('financials-tab: Table rendered with', tableData.length, 'rows');
            } else {
                console.warn('financials-tab: Table component not initialized yet, will retry...');
                setTimeout(() => {
                    if (this.tableContainer.components && this.tableContainer.components.table) {
                        this.tableContainer.components.table.tableData = tableData;
                        this.tableContainer.setAttribute('table', 'data', JSON.stringify(tableData));
                        this.tableContainer.components.table.renderTable();
                        console.log('financials-tab: Table rendered on retry');
                    }
                }, 200);
            }
        }, 150);

        this.contentArea.appendChild(this.tableContainer);
    },

    hideFinancialsTable: function () {
        console.log('financials-tab: Hiding financials table');

        if (!this.contentArea) {
            return;
        }

        // Clear table
        this.contentArea.innerHTML = '';
        this.tableContainer = null;

        // Recreate blurFrame and button (normal state)
        this.createContentArea();
    },

    updateFinancialsTable: function () {
        if (!this.tableContainer || !this.isExpanded) {
            console.log('financials-tab: No table to update or panel not expanded');
            return;
        }

        console.log('financials-tab: Updating table with current data');

        // Prepare new table data
        const tableData = this.prepareTableData(this.data.financialsData, this.selectedMainTab, this.selectedPeriodTab);

        if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
            console.error('financials-tab: No valid table data for update!');
            return;
        }

        // Update table component
        if (this.tableContainer.components && this.tableContainer.components.table) {
            this.tableContainer.components.table.tableData = tableData;
            this.tableContainer.setAttribute('table', 'data', JSON.stringify(tableData));
            this.tableContainer.components.table.renderTable();
            console.log('financials-tab: Table updated with', tableData.length, 'rows');
        } else {
            console.warn('financials-tab: Table component not found, will retry...');
            setTimeout(() => {
                if (this.tableContainer.components && this.tableContainer.components.table) {
                    this.tableContainer.components.table.tableData = tableData;
                    this.tableContainer.setAttribute('table', 'data', JSON.stringify(tableData));
                    this.tableContainer.components.table.renderTable();
                    console.log('financials-tab: Table updated on retry');
                }
            }, 100);
        }
    },

    // Keep old method for compatibility but redirect to new method
    openFinancialsPopup: function () {
        console.log('financials-tab: openFinancialsPopup called (deprecated, redirecting to expandFinancialsPanel)');
        this.expandFinancialsPanel();
    },

    // DEPRECATED: createFinancialsPopup and closeFinancialsPopup are no longer used
    // They are kept for compatibility but should not be called
    // Use expandFinancialsPanel() and collapseFinancialsPanel() instead

    moveCentralPanelToCenter: function () {
        console.log('financials-tab: Moving central panel to center position');

        // Find main-elements-moving component
        const mainElementsMovingEl = document.querySelector('[main-elements-moving]');
        if (!mainElementsMovingEl || !mainElementsMovingEl.components || !mainElementsMovingEl.components['main-elements-moving']) {
            console.warn('financials-tab: main-elements-moving component not found');
            return false;
        }

        const mainElementsMoving = mainElementsMovingEl.components['main-elements-moving'];
        const currentStep = window.VRPanelSlotManager ? window.VRPanelSlotManager.getStep() : mainElementsMoving.data.currentStep;
        const isCenter = window.VRPanelSlotManager ? (window.VRPanelSlotManager.getSlotForPanel('CENTER_BLOCK') === 'CENTER') : (currentStep === 0);

        console.log('financials-tab: Current step:', currentStep, '| isCenter:', isCenter);

        // If central panel is not in center (step 0), move it to center
        if (!isCenter) {
            console.log('financials-tab: Central panel is not in center, moving to center...');

            // Determine direction to move based on current step
            // Step 1: central panel is at right position, need to move left (decreasing)
            // Step 2: central panel is at left position, need to move right (increasing) to get to 0
            let increasing = false;
            if (currentStep === 1) {
                // Need to go back 1 step (click left arrow, decreasing)
                increasing = false;
            } else if (currentStep === 2) {
                // Need to go forward 1 step (click right arrow, increasing) to get to 0
                increasing = true;
            }

            // Use handleClickOnArrow to properly move panels
            // Temporarily disable the checkIfChartNotBlocked check and reset lastClickTime
            const originalCheck = mainElementsMoving.checkIfChartNotBlocked;
            const originalLastClickTime = mainElementsMoving.lastClickTime;
            mainElementsMoving.checkIfChartNotBlocked = () => false;
            mainElementsMoving.lastClickTime = 0; // Reset to allow immediate execution

            // Call handleClickOnArrow to move panels
            mainElementsMoving.handleClickOnArrow(increasing);

            // Restore original values
            mainElementsMoving.checkIfChartNotBlocked = originalCheck;
            mainElementsMoving.lastClickTime = originalLastClickTime;

            console.log('financials-tab: Central panel moved to center position');
            return true; // Panel was moved
        } else {
            console.log('financials-tab: Central panel is already in center position');
            return false; // Panel was not moved
        }
    },

    animateDockDown: function () {
        const dock = document.getElementById('dock');
        if (!dock) {
            console.warn('financials-tab: Dock element not found');
            return;
        }

        // Clean up any existing animations first
        dock.removeAttribute('animation__moveDown');
        dock.removeAttribute('animation__moveUp');

        // Store original position if not already stored, or update if dock is visible and at original position
        const currentPos = dock.getAttribute('position');
        const currentPosParsed = {
            x: parseFloat(currentPos.x) || 0,
            y: parseFloat(currentPos.y) || 0,
            z: parseFloat(currentPos.z) || 0
        };

        // If dock is visible, it should be at original position - update stored position
        const isVisible = dock.getAttribute('visible') !== false;
        if (!this.dockOriginalPosition || isVisible) {
            this.dockOriginalPosition = { ...currentPosParsed };
            console.log('financials-tab: Stored/updated dock original position:', this.dockOriginalPosition);
        }

        // Get dock-visual element for opacity animation
        const dockVisual = dock.querySelector('#dock-visual');

        // Clean up dock-visual animations
        if (dockVisual) {
            dockVisual.removeAttribute('animation__fadeOut');
            dockVisual.removeAttribute('animation__fadeIn');
        }

        // Ensure dock is visible before animating
        if (!isVisible) {
            dock.setAttribute('visible', true);
            Array.from(dock.children).forEach(child => {
                child.setAttribute('visible', true);
            });
        }

        // Set current opacity if needed
        if (dockVisual) {
            const currentOpacity = dockVisual.getAttribute('opacity');
            if (!currentOpacity || currentOpacity === '0') {
                dockVisual.setAttribute('opacity', 1);
            }
        }

        // Calculate target position (move down by 1 unit)
        const targetY = this.dockOriginalPosition.y - 1;
        const targetPosition = `${this.dockOriginalPosition.x} ${targetY} ${this.dockOriginalPosition.z}`;

        // Animate position down
        dock.setAttribute('animation__moveDown', {
            property: 'position',
            to: targetPosition,
            dur: 1500,
            easing: 'easeInOutCubic'
        });

        // Animate opacity to 0 (fade out) on dock-visual if it exists
        if (dockVisual) {
            dockVisual.setAttribute('animation__fadeOut', {
                property: 'opacity',
                to: 0,
                dur: 1500,
                easing: 'easeInOutCubic'
            });
        }

        // Hide dock and all children after animation completes
        const hideDockHandler = () => {
            dock.setAttribute('visible', false);
            // Hide all children
            Array.from(dock.children).forEach(child => {
                child.setAttribute('visible', false);
            });
            dock.removeEventListener('animationcomplete__moveDown', hideDockHandler);
        };
        dock.removeEventListener('animationcomplete__moveDown', hideDockHandler); // Remove if exists
        dock.addEventListener('animationcomplete__moveDown', hideDockHandler, { once: true });
    },

    animateDockUp: function () {
        const dock = document.getElementById('dock');
        if (!dock) {
            console.warn('financials-tab: Dock element not found');
            return;
        }

        if (!this.dockOriginalPosition) {
            console.warn('financials-tab: Dock original position not stored, cannot animate back');
            return;
        }

        // Clean up any existing animations first
        dock.removeAttribute('animation__moveDown');
        dock.removeAttribute('animation__moveUp');

        // Get dock-visual element for opacity animation
        const dockVisual = dock.querySelector('#dock-visual');

        // Clean up dock-visual animations
        if (dockVisual) {
            dockVisual.removeAttribute('animation__fadeOut');
            dockVisual.removeAttribute('animation__fadeIn');
        }

        // Get current position to determine starting point
        const currentPos = dock.getAttribute('position');
        const currentY = parseFloat(currentPos.y) || 0;

        // Show dock and all children first
        dock.setAttribute('visible', true);
        Array.from(dock.children).forEach(child => {
            child.setAttribute('visible', true);
        });

        // Set initial opacity to 0
        if (dockVisual) {
            dockVisual.setAttribute('opacity', 0);
        }

        // Set initial position (below original) - use current position if it's already below
        const initialY = currentY < this.dockOriginalPosition.y ? currentY : (this.dockOriginalPosition.y - 1);
        dock.setAttribute('position', `${this.dockOriginalPosition.x} ${initialY} ${this.dockOriginalPosition.z}`);

        // Target position (original position)
        const targetPosition = `${this.dockOriginalPosition.x} ${this.dockOriginalPosition.y} ${this.dockOriginalPosition.z}`;

        // Animate position up
        dock.setAttribute('animation__moveUp', {
            property: 'position',
            to: targetPosition,
            dur: 1500,
            easing: 'easeInOutCubic'
        });

        // Animate opacity to 1 (fade in) on dock-visual if it exists
        if (dockVisual) {
            dockVisual.setAttribute('animation__fadeIn', {
                property: 'opacity',
                to: 1,
                dur: 1500,
                easing: 'easeInOutCubic'
            });
        }
    },

    // DEPRECATED: updateFinancialsPopup is no longer used
    // Use updateFinancialsTable() instead

    // Prepare table data from financials data based on selected tabs
    prepareTableData: function (financialsData, mainTabIndex, periodTabIndex) {
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📊 financials-tab: prepareTableData called');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📋 Input parameters:', {
            mainTabIndex,
            periodTabIndex,
            financialsDataType: typeof financialsData,
            isObject: typeof financialsData === 'object' && financialsData !== null
        });

        console.log('📦 Full financialsData structure:', JSON.stringify(financialsData, null, 2));

        console.log('🔍 Financials data keys:', {
            hasIncome: !!financialsData?.income,
            hasBalance: !!financialsData?.balance,
            hasCashflow: !!financialsData?.cashflow,
            incomeKeys: financialsData?.income ? Object.keys(financialsData.income) : [],
            balanceKeys: financialsData?.balance ? Object.keys(financialsData.balance) : [],
            cashflowKeys: financialsData?.cashflow ? Object.keys(financialsData.cashflow) : []
        });

        // Log income data structure
        if (financialsData?.income) {
            console.log('💰 Income Statement structure:', {
                hasAnnual: !!financialsData.income.annual,
                hasQuarterly: !!financialsData.income.quarterly,
                hasTTM: !!financialsData.income.ttm,
                annualLength: financialsData.income.annual?.length || 0,
                quarterlyLength: financialsData.income.quarterly?.length || 0,
                ttmType: typeof financialsData.income.ttm,
                ttmKeys: financialsData.income.ttm ? Object.keys(financialsData.income.ttm) : []
            });
            if (financialsData.income.annual?.length > 0) {
                console.log('💰 Income Annual sample (first record):', financialsData.income.annual[0]);
            }
            if (financialsData.income.quarterly?.length > 0) {
                console.log('💰 Income Quarterly sample (first record):', financialsData.income.quarterly[0]);
            }
            if (financialsData.income.ttm) {
                console.log('💰 Income TTM data:', financialsData.income.ttm);
            }
        }

        // Log balance data structure
        if (financialsData?.balance) {
            console.log('🏦 Balance Sheet structure:', {
                hasAnnual: !!financialsData.balance.annual,
                hasQuarterly: !!financialsData.balance.quarterly,
                hasTTM: !!financialsData.balance.ttm,
                annualLength: financialsData.balance.annual?.length || 0,
                quarterlyLength: financialsData.balance.quarterly?.length || 0,
                ttmType: typeof financialsData.balance.ttm,
                ttmKeys: financialsData.balance.ttm ? Object.keys(financialsData.balance.ttm) : []
            });
            if (financialsData.balance.annual?.length > 0) {
                console.log('🏦 Balance Annual sample (first record):', financialsData.balance.annual[0]);
            }
            if (financialsData.balance.ttm) {
                console.log('🏦 Balance TTM data:', financialsData.balance.ttm);
            }
        }

        // Log cashflow data structure
        if (financialsData?.cashflow) {
            console.log('💵 Cash Flow structure:', {
                hasAnnual: !!financialsData.cashflow.annual,
                hasQuarterly: !!financialsData.cashflow.quarterly,
                hasTTM: !!financialsData.cashflow.ttm,
                annualLength: financialsData.cashflow.annual?.length || 0,
                quarterlyLength: financialsData.cashflow.quarterly?.length || 0,
                ttmType: typeof financialsData.cashflow.ttm,
                ttmKeys: financialsData.cashflow.ttm ? Object.keys(financialsData.cashflow.ttm) : []
            });
            if (financialsData.cashflow.annual?.length > 0) {
                console.log('💵 Cashflow Annual sample (first record):', financialsData.cashflow.annual[0]);
            }
            if (financialsData.cashflow.ttm) {
                console.log('💵 Cashflow TTM data:', financialsData.cashflow.ttm);
            }
        }

        if (!financialsData || typeof financialsData !== 'object') {
            console.warn('financials-tab: No financials data available or invalid type:', typeof financialsData);
            return [['Breakdown', 'TTM', 'Period 1', 'Period 2', 'Period 3', 'Period 4'], ['No data', '', '', '', '', '']];
        }

        if (!financialsData.income && !financialsData.balance && !financialsData.cashflow) {
            console.warn('financials-tab: Financials data structure is empty', {
                keys: Object.keys(financialsData),
                income: financialsData.income,
                balance: financialsData.balance,
                cashflow: financialsData.cashflow
            });
            return [['Breakdown', 'TTM', 'Period 1', 'Period 2', 'Period 3', 'Period 4'], ['No data', '', '', '', '', '']];
        }

        const mainTabTypes = ['income', 'balance', 'cashflow'];
        const periodTypes = ['annual', 'quarterly'];

        const tabType = mainTabTypes[mainTabIndex] || 'income';
        const periodType = periodTypes[periodTabIndex] || 'annual';

        // Get data - handle both array and object formats
        let data = financialsData[tabType]?.[periodType];
        let quarterlyData = financialsData[tabType]?.quarterly;

        console.log(`financials-tab: Raw data for ${tabType}/${periodType}:`, data);
        console.log(`financials-tab: Raw quarterly data for ${tabType}:`, quarterlyData);
        console.log(`financials-tab: Data type before conversion:`, typeof data, Array.isArray(data));

        // Convert to array if it's an object
        if (data && !Array.isArray(data)) {
            console.log(`financials-tab: Converting ${tabType}/${periodType} from object to array`);
            if (typeof data === 'object' && data !== null) {
                // If it's an object with numeric keys or period keys, convert to array
                data = Object.values(data);
                console.log(`financials-tab: Converted to array:`, data);
            } else {
                console.warn(`financials-tab: Data is not a valid object, setting to empty array`);
                data = [];
            }
        }

        if (quarterlyData && !Array.isArray(quarterlyData)) {
            console.log(`financials-tab: Converting ${tabType}/quarterly from object to array`);
            if (typeof quarterlyData === 'object' && quarterlyData !== null) {
                quarterlyData = Object.values(quarterlyData);
                console.log(`financials-tab: Converted quarterly to array:`, quarterlyData);
            } else {
                console.warn(`financials-tab: Quarterly data is not a valid object, setting to empty array`);
                quarterlyData = [];
            }
        }

        // Ensure arrays
        data = Array.isArray(data) ? data : [];
        quarterlyData = Array.isArray(quarterlyData) ? quarterlyData : [];

        console.log(`financials-tab: Final data for ${tabType}/${periodType}:`, data);
        console.log(`financials-tab: Data type:`, Array.isArray(data) ? 'Array' : typeof data);
        console.log(`financials-tab: Data length:`, data.length);
        console.log(`financials-tab: Quarterly data length:`, quarterlyData.length);

        if (data.length > 0) {
            console.log(`📅 First period sample for ${tabType}/${periodType}:`, data[0]);
            console.log(`🔑 First period keys:`, Object.keys(data[0]));
            console.log(`📊 First period values:`, {
                revenue: data[0].revenue,
                costOfRevenue: data[0].costOfRevenue,
                grossProfit: data[0].grossProfit,
                operatingExpense: data[0].operatingExpense,
                operatingIncome: data[0].operatingIncome,
                nonOperatingIncome: data[0].nonOperatingIncome,
                interestIncome: data[0].interestIncome,
                depreciationAmortization: data[0].depreciationAmortization,
                pretaxIncome: data[0].pretaxIncome,
                taxes: data[0].taxes,
                netIncome: data[0].netIncome,
                eps: data[0].eps,
                ebit: data[0].ebit,
                ebitda: data[0].ebitda,
                period: data[0].period
            });
        }

        let maxPeriods = 4;
        // Take first N periods (newest first) - no reverse needed
        const periods = data.slice(0, maxPeriods);

        console.log(`financials-tab: Selected periods (newest first):`, periods.map(p => p.period));

        if (periods.length === 0) {
            console.warn(`financials-tab: No periods found for ${tabType}/${periodType}`);
            return [['Breakdown', 'TTM', 'Period 1', 'Period 2', 'Period 3', 'Period 4'], ['No data available', '', '', '', '', '']];
        }

        // Get TTM data: First try to use normalized TTM from API if available, otherwise calculate from quarters
        let ttmData = null;

        console.log('🔍 Checking for TTM data...');
        console.log(`   tabType: ${tabType}`);
        console.log(`   financialsData[${tabType}]:`, financialsData[tabType]);
        console.log(`   financialsData[${tabType}]?.ttm:`, financialsData[tabType]?.ttm);
        console.log(`   quarterlyData length:`, quarterlyData?.length);

        // Check if TTM is provided directly in the normalized data structure (already normalized by RightPanelProxyAPIManager)
        const ttmFromAPI = financialsData[tabType]?.ttm;
        if (ttmFromAPI && typeof ttmFromAPI === 'object') {
            console.log(`✅ Using normalized TTM from API for ${tabType}:`, ttmFromAPI);
            console.log(`📊 TTM data keys:`, Object.keys(ttmFromAPI));
            console.log(`📊 TTM data values:`, {
                revenue: ttmFromAPI.revenue,
                costOfRevenue: ttmFromAPI.costOfRevenue,
                grossProfit: ttmFromAPI.grossProfit,
                operatingExpense: ttmFromAPI.operatingExpense,
                operatingIncome: ttmFromAPI.operatingIncome,
                nonOperatingIncome: ttmFromAPI.nonOperatingIncome,
                interestIncome: ttmFromAPI.interestIncome,
                depreciationAmortization: ttmFromAPI.depreciationAmortization,
                pretaxIncome: ttmFromAPI.pretaxIncome,
                taxes: ttmFromAPI.taxes,
                netIncome: ttmFromAPI.netIncome,
                eps: ttmFromAPI.eps,
                ebit: ttmFromAPI.ebit,
                ebitda: ttmFromAPI.ebitda
            });
            // TTM is already normalized, use it directly
            ttmData = ttmFromAPI;
        } else if (quarterlyData && quarterlyData.length >= 4) {
            // Calculate TTM from last 4 quarters if TTM not provided
            const last4Quarters = quarterlyData.slice(0, 4);
            console.log(`⚠️ TTM not found in API, calculating from quarters:`, last4Quarters.map(q => q.period));
            ttmData = this.calculateTTM(last4Quarters, tabType);
            console.log(`📊 TTM calculated from quarters:`, ttmData);
        } else {
            console.warn(`❌ Not enough quarterly data for TTM calculation (have ${quarterlyData?.length || 0}, need 4)`);
        }

        // Format number helper - all numbers in thousands, format with commas
        // Special handling for EPS (earnings per share) - keep decimals
        // For large numbers (9+ digits), format with M (millions), B (billions), T (trillions)
        const formatNumber = (value, isEPS = false) => {
            if (value === null || value === undefined) {
                return '--';
            }
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            if (isNaN(numValue)) {
                return '--';
            }
            if (numValue === 0) return '0';

            // For EPS, keep 2 decimal places
            if (isEPS) {
                return numValue.toFixed(2);
            }

            // Get absolute value for formatting
            const absValue = Math.abs(numValue);
            const sign = numValue < 0 ? '-' : '';

            // Count number of digits (handle edge cases)
            let numDigits = 1;
            if (absValue >= 1) {
                numDigits = Math.floor(Math.log10(absValue)) + 1;
            }

            if (absValue >= 1000000000) {
                const trillions = absValue / 1000000000;
                return `${sign}${trillions.toFixed(2)}B`;
            }

            else if (absValue >= 1000000) {
                const billions = absValue / 1000000;
                return `${sign}${billions.toFixed(2)}M`;
            }

            // For values with less than 1,000 thousands, format with commas
            return `${sign}${Math.round(absValue).toLocaleString('en-US')}`;
        };

        // Format period date - format like "9/30/2024" or "12/31/2024"
        const formatPeriod = (periodString) => {
            if (!periodString) return 'N/A';
            // Handle both "YYYY-MM-DD" and Date object formats
            const date = new Date(periodString);
            if (isNaN(date.getTime())) {
                // If parsing fails, try to return as-is or format differently
                return periodString;
            }
            // Format as M/D/YYYY (e.g., "9/30/2024" or "12/31/2024")
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        };

        // Build table rows based on tab type
        const tableData = [];

        // Header row - show TTM and periods (newest first)
        const headerRow = ['Breakdown', 'TTM'];
        periods.forEach((period) => {
            headerRow.push(formatPeriod(period.period));
        });
        // Pad with empty cells if needed (for consistent table width)
        const expectedColumns = maxPeriods + 2; // TTM + periods + Breakdown
        while (headerRow.length < expectedColumns) {
            headerRow.push('');
        }
        tableData.push(headerRow);

        // Helper function to add a row with optional indentation
        const addRow = (label, key, indent = 0, ttmValue = null) => {
            const indentPrefix = indent > 0 ? '  '.repeat(indent) : '';
            const row = [indentPrefix + label];

            // Check if this is EPS field
            const isEPS = key === 'eps';

            // TTM value
            if (ttmValue !== null) {
                row.push(formatNumber(ttmValue, isEPS));
            } else if (ttmData && ttmData[key] !== undefined) {
                row.push(formatNumber(ttmData[key], isEPS));
            } else {
                row.push('--');
            }

            // Period values - iterate through periods in order (newest first)
            periods.forEach((period, periodIndex) => {
                const value = period[key];
                console.log(`financials-tab: addRow - ${label} (${key}), period ${periodIndex}:`, {
                    value,
                    valueType: typeof value,
                    isNull: value === null,
                    isUndefined: value === undefined,
                    period: period.period
                });
                row.push(formatNumber(value, isEPS));
            });

            // Pad if needed (for consistent table width)
            const expectedColumns = maxPeriods + 2; // TTM + periods + Breakdown
            while (row.length < expectedColumns) {
                row.push('');
            }
            tableData.push(row);
        };

        // Helper to calculate Gross Profit if not provided
        const getGrossProfit = (period) => {
            if (period.grossProfit !== null && period.grossProfit !== undefined && !isNaN(period.grossProfit)) {
                return period.grossProfit;
            }
            // Calculate if revenue and costOfRevenue are available
            if (period.revenue !== null && period.revenue !== undefined &&
                period.costOfRevenue !== null && period.costOfRevenue !== undefined) {
                const revenue = parseFloat(period.revenue);
                const cost = parseFloat(period.costOfRevenue);
                if (!isNaN(revenue) && !isNaN(cost)) {
                    return revenue - cost;
                }
            }
            return null;
        };

        // Data rows based on tab type with hierarchical structure
        if (tabType === 'income') {
            // Income Statement structure
            addRow('Total Revenue', 'revenue');
            addRow('Cost of Revenue', 'costOfRevenue');

            // Gross Profit - calculate if needed
            let grossProfitTTM = null;
            if (ttmData) {
                if (ttmData.grossProfit !== null && ttmData.grossProfit !== undefined && !isNaN(ttmData.grossProfit)) {
                    grossProfitTTM = ttmData.grossProfit;
                } else if (ttmData.revenue !== null && ttmData.revenue !== undefined &&
                    ttmData.costOfRevenue !== null && ttmData.costOfRevenue !== undefined) {
                    const ttmRevenue = parseFloat(ttmData.revenue);
                    const ttmCost = parseFloat(ttmData.costOfRevenue);
                    if (!isNaN(ttmRevenue) && !isNaN(ttmCost)) {
                        grossProfitTTM = ttmRevenue - ttmCost;
                    }
                }
            }
            const grossProfitRow = ['Gross Profit'];
            grossProfitRow.push(formatNumber(grossProfitTTM, false)); // Not EPS
            periods.forEach(period => {
                grossProfitRow.push(formatNumber(getGrossProfit(period), false)); // Not EPS
            });
            const expectedColumns = maxPeriods + 2; // TTM + periods + Breakdown
            while (grossProfitRow.length < expectedColumns) grossProfitRow.push('');
            tableData.push(grossProfitRow);

            addRow('Operating Expense', 'operatingExpense');
            addRow('Operating Income/Loss', 'operatingIncome');

            // Non-operating income/loss section
            addRow('Non-operating In/L', 'nonOperatingIncome');
            addRow('Interest Income/Loss', 'interestIncome', 1);
            addRow('Depreciation/Amortization', 'depreciationAmortization', 1);

            addRow('Pretax Income/Loss', 'pretaxIncome');
            addRow('Taxes', 'taxes');
            addRow('Net Income/Loss', 'netIncome');
            addRow('EPS', 'eps');
            addRow('EBIT', 'ebit');
            addRow('EBITDA', 'ebitda');

        } else if (tabType === 'balance') {
            // Balance Sheet structure
            addRow('Total Assets', 'totalAssets');
            addRow('Total Liabilities', 'totalLiabilities');
            addRow('Total Equity', 'totalEquity');
            addRow('Current Assets', 'currentAssets');
            addRow('Current Liabilities', 'currentLiabilities');
            addRow('Cash and Equivalents', 'cashAndCashEquivalents');
            addRow('Short-Term Investments', 'shortTermInvestments');
            addRow('Accounts Receivable', 'accountsReceivable');
            addRow('Inventory', 'inventory');
            addRow('Long-Term Investments', 'longTermInvestments');
            addRow('Property, Plant & Equip...', 'propertyPlantEquipment');
            addRow('Goodwill', 'goodwill');
            addRow('Intangible Assets', 'intangibleAssets');
            
            // addRow('Cash', 'cash', 2);
            // addRow('Non-current Assets', 'nonCurrentAssets', 1);

            // addRow('Total Liabilities', 'totalLiabilities');
            // addRow('Current Liabilities', 'currentLiabilities', 1);
            // addRow('Non-current Liabilities', 'nonCurrentLiabilities', 1);
            //
            // addRow('Total Equity', 'totalEquity');
            // addRow('Common Stock Equity', 'commonStockEquity', 1);

        } else if (tabType === 'cashflow') {
            // Cash Flow structure
            addRow('Operating Cash Flow', 'operatingCashFlow');
            addRow('Investing Cash Flow', 'investingCashFlow');
            addRow('Financing Cash Flow', 'financingCashFlow');
            addRow('Net Cash Flow', 'netCashFlow');
            addRow('Free Cash Flow', 'freeCashFlow');
            addRow('Capital Expenditure', 'capitalExpenditure');
            addRow('Dividends Paid', 'netDividendsPaid');
            addRow('Stock Repurchases', 'stockRepurchases');
            addRow('Debt Issuance', 'debtIssuance');
            addRow('Debt Repayment', 'debtRepayment');
        }

        return tableData;
    },

    // Calculate TTM (Trailing Twelve Months) from last 4 quarters
    calculateTTM: function (quarters, tabType) {
        if (!quarters || quarters.length === 0) {
            console.warn('financials-tab: calculateTTM called with empty quarters array');
            return null;
        }

        const ttm = {};

        // Sum all numeric fields from the quarters
        quarters.forEach((quarter, index) => {
            if (!quarter || typeof quarter !== 'object') {
                console.warn(`financials-tab: Invalid quarter at index ${index}:`, quarter);
                return;
            }

            Object.keys(quarter).forEach(key => {
                if (key !== 'period' && quarter[key] !== null && quarter[key] !== undefined) {
                    const value = parseFloat(quarter[key]);
                    if (!isNaN(value)) {
                        ttm[key] = (ttm[key] || 0) + value;
                    }
                }
            });
        });

        console.log('financials-tab: TTM calculation result:', ttm);
        return ttm;
    },

    updateStockSelectionState: function () {
        // Re-find noStockMessage if not found
        if (!this.noStockMessage) {
            this.noStockMessage = this.el.querySelector('.no-stock-selected-message');
            console.log('financials-tab: Re-queried noStockMessage in updateStockSelectionState:', !!this.noStockMessage);
        }

        // Check if stock is selected
        // Try to get from right-panel-data-handler first
        let hasStock = false;
        const rightPanelDataHandler = document.querySelector('[right-panel-data-handler]');
        if (rightPanelDataHandler && rightPanelDataHandler.components && rightPanelDataHandler.components['right-panel-data-handler']) {
            const ticker = rightPanelDataHandler.components['right-panel-data-handler'].currentTicker;
            hasStock = !!ticker;
        }

        // Fallback: check if ticker exists and is not default
        if (!hasStock && this.data.ticker) {
            hasStock = this.data.ticker !== 'AAPL' &&
                this.data.ticker.trim() !== '' &&
                this.data.ticker !== this.schema.ticker.default;
        }

        console.log('financials-tab: updateStockSelectionState - hasStock:', hasStock, 'noStockMessage:', !!this.noStockMessage);

        if (this.noStockMessage) {
            this.noStockMessage.setAttribute('visible', !hasStock);
            
            // Update color based on theme
            const isLight = this.themeMode === 'Light';
            const msgColor = isLight ? '#14141C' : '#ffffff';
            
            // Check if noStockMessage is a text element or contains one
            if (this.noStockMessage.tagName.toLowerCase() === 'a-text') {
                this.noStockMessage.setAttribute('color', msgColor);
            } else {
                const textEl = this.noStockMessage.querySelector('a-text');
                if (textEl) textEl.setAttribute('color', msgColor);
            }
            
            console.log('financials-tab: Set noStockMessage visible to:', !hasStock, 'color:', msgColor);
        } else {
            console.warn('financials-tab: noStockMessage element not found!');
        }

        // Helper to toggle raycastable on elements
        const toggleRaycastable = (container, enable) => {
            if (!container) return;
            const clickables = container.querySelectorAll('.clickable, [data-raycastable], [ui-button]');
            clickables.forEach(el => {
                if (enable) {
                   // Only add raycastable if it's supposed to be interactive
                   if (el.hasAttribute('ui-button') || el.classList.contains('clickable-disabled')) {
                       el.setAttribute('data-raycastable', '');
                   }
                   if (el.classList.contains('clickable-disabled')) {
                       el.classList.remove('clickable-disabled');
                       el.classList.add('clickable');
                   }
                   // Restore ui-button interactivity
                   if (el.hasAttribute('ui-button')) {
                       el.setAttribute('data-raycastable', '');
                   }
                } else {
                   el.removeAttribute('data-raycastable');
                   if (el.classList.contains('clickable')) {
                       el.classList.remove('clickable');
                       el.classList.add('clickable-disabled');
                   }
                }
            });
        };

        // Show/hide content elements and toggle interactivity
        if (this.mainNavigation) {
            this.mainNavigation.setAttribute('visible', hasStock);
            toggleRaycastable(this.mainNavigation, hasStock);
        }
        if (this.periodNavigation) {
            this.periodNavigation.setAttribute('visible', hasStock);
            toggleRaycastable(this.periodNavigation, hasStock);
        }
        if (this.contentArea) {
            this.contentArea.setAttribute('visible', hasStock);
            toggleRaycastable(this.contentArea, hasStock);
        }
        if (this.incomeContent) {
            this.incomeContent.setAttribute('visible', hasStock ? false : false);
        }
        if (this.balanceContent) {
            this.balanceContent.setAttribute('visible', hasStock ? false : false);
        }
        if (this.cashflowContent) {
            this.cashflowContent.setAttribute('visible', hasStock ? false : false);
        }
    }
});

